import PropTypes from "prop-types";
import React, { Component } from "react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listGridPlugin from "@fullcalendar/list";
import scheduleViewPlugin from "./schedule-view-plugin";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "../../scss/calendar.scss";
import clsx from "clsx";
import withStyles from "@mui/styles/withStyles";
import { Typography, Box } from "@mui/material";
import { sortBy } from "lodash";

const styles = theme => ({
  "@global": {
    ".fc-center": {
      color: theme.palette.text.secondary
    },
    "div.fc-toolbar": {
      "& .fc-left, .fc-right": {
        "& .fc-button": {
          "&.fc-prev-button, &.fc-next-button": {
            border: "none",
            color: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.action.hover
            },
            [theme.breakpoints.down("sm")]: {
              marginRight: theme.spacing(0.5)
            }
          }
        },
        [theme.breakpoints.down("sm")]: {
          marginLeft: 0
        }
      },
      "& .fc-right": {
        [theme.breakpoints.down("sm")]: {
          justifyContent: "center"
        }
      }
    },
    "div.fc-content, .fc-event": {
      color: theme.palette.text.secondary
    },
    ".fc-button": {
      border: `solid 1px ${theme.palette.secondary[300]}`,
      color: theme.palette.action.active,
      "&:hover": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.main
      },
      "&.fc-button-primary:not(:disabled).fc-button-active": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
        border: `solid 1px ${theme.palette.primary.main}`
      }
    },
    ".fc-button-primary:not(:disabled).fc-button-active, .fc-button-primary:not(:disabled):active": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.main
    },
    ".timeGrid-col-header.active": {
      "& .timeGrid-col-header-day": {
        color: theme.palette.primary.main
      },
      "& .timeGrid-col-header-date": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light
      }
    },
    "td.fc-axis.fc-widget-content": {
      color: theme.palette.text.secondary,
      "& > span": {
        backgroundColor: theme.palette.common.white
      }
    },
    ".fc-day-number": {
      color: theme.palette.text.secondary
    },
    ".control-panel-calendar": {
      "& .fc-event": {
        // Important necessary to override calendar color
        backgroundColor: `${theme.palette.primary.main} !important`,
        borderColor: `${theme.palette.primary.main} !important`
      },
      "& .fc-today .fc-day-number": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light
      },
      "& .fc-more-cell > div": {
        backgroundColor: theme.palette.primary.main
      }
    },
    ".schedule-view .fc-header-toolbar .fc-right": {
      justifyContent: "flex-end",
      "& .fc-scheduleView-button": {
        color: `${theme.palette.primary.main}`,
        border: `solid 1px ${theme.palette.primary.main}`,
        backgroundColor: `${theme.palette.primary.light}`
      }
    },
    ".calendar-view .fc-calendarView-button": {
      color: `${theme.palette.primary.main}`,
      border: `solid 1px ${theme.palette.primary.main}`,
      backgroundColor: `${theme.palette.primary.light}`,
      zIndex: 1
    }
  }
});

class Calendar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scheduleView: false,
      clickedDate: null,
      events: []
    };
    this.calendarRef = React.createRef();
  }

  showScheduleView() {
    if (this.calendarRef.current && this.state.scheduleView) {
      this.calendarRef.current.calendar.changeView("scheduleByDate");
    } else {
      this.calendarRef.current.calendar.changeView("dayGridMonth");
    }
  }

  showClickedDate() {
    const { clickedDate } = this.state;
    if (this.calendarRef.current && clickedDate) {
      this.calendarRef.current.calendar.changeView(
        "timeGridDay",
        clickedDate.dateStr
      );
    }
  }

  componentDidUpdate(existingProps) {
    const { scheduleView, clickedDate, events } = this.props;
    if (scheduleView !== this.state.scheduleView) {
      this.setState({ scheduleView }, () => {
        this.showScheduleView();
      });
    }

    if (clickedDate !== this.state.clickedDate) {
      this.setState({ clickedDate }, () => {
        this.showClickedDate();
      });
    }
    if (events !== existingProps.events) {
      this.setState({ events: sortBy(events, event => new Date(event.start)) });
    }
  }
  handleMouseEnter = event => {
    if (this.props.onMouseEnter) {
      this.props.onMouseEnter(event);
    }
  };

  render() {
    const {
      fullScreen,
      viewRendered,
      header,
      customButtons,
      columnHeaderHtml,
      eventRender,
      handleEventClicked,
      eventPositioned,
      eventLimit,
      handleDateClicked,
      locale,
      scheduleView,
      calendars,
      printTitle
    } = this.props;

    const { events } = this.state;

    return (
      <div
        data-qa="learning-center-calendar"
        className={clsx("learning-center-calendar", {
          "full-screen": fullScreen,
          "schedule-view": scheduleView,
          "calendar-view": !scheduleView
        })}
      >
        <Box className="print-title">
          <Typography variant="h2">{printTitle}</Typography>
        </Box>
        <FullCalendar
          eventRender={eventRender}
          eventPositioned={eventPositioned}
          scrollTime="7:00:00"
          allDaySlot={false}
          columnHeaderHtml={columnHeaderHtml}
          customButtons={customButtons}
          defaultView="dayGridMonth"
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            listGridPlugin,
            interactionPlugin,
            scheduleViewPlugin
          ]}
          events={events}
          datesRender={viewRendered}
          slotDuration="01:00:00"
          viewSkeletonRender={viewRendered}
          header={header}
          fixedWeekCount={false}
          ref={this.calendarRef}
          views={{
            dayGrid: {
              eventLimit
            },
            schedule: {
              calendars
            }
          }}
          dateClick={handleDateClicked}
          eventClick={handleEventClicked}
          locale={locale}
          eventMouseEnter={this.handleMouseEnter}
        />
      </div>
    );
  }
}

Calendar.propTypes = {
  calendars: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      color: PropTypes.string
    })
  ),
  events: PropTypes.array,
  /**
   * An object specifying the buttons that the header will contain
   */
  header: PropTypes.any,
  viewRendered: PropTypes.func,
  eventRender: PropTypes.func,
  eventPositioned: PropTypes.func,
  /**
   * The function that will return an HTML string for the column header
   */
  columnHeaderHtml: PropTypes.func,
  /**
   * The number of events to be shown in a day
   */
  eventLimit: PropTypes.number,
  handleDateClicked: PropTypes.func,
  handleEventClicked: PropTypes.func,
  clickedDate: PropTypes.object
};

Calendar.defaultProps = {
  eventLimit: 2
};

export default withStyles(styles, { withTheme: true })(Calendar);
